/* eslint-disable @wyz/module/no-relative-import */
import { ReactNode } from 'react';
import { Datatable, Icon, Text, Title, useDatatableContext } from '@wyz/ui';
import { serialize } from '@wyz/utils';
import { useApi, useConfig, Trans } from '~/common/context';
import { ConnectQueryKeys } from '~/connect/api';
import { IChangeLog, IChangeLogList } from '~/connect/types';
import { ChangeLogEntry } from './change-log-entry';
import './change-logs-table.scss';

const SORT_FIELD = 'loggedAt';

function AmendedOnHeader() {
  const { internals } = useDatatableContext<IChangeLog>();
  const { sort } = internals.state;
  const isActive = sort?.sortField === SORT_FIELD;

  const handleSort = () => {
    const nextDirection =
      !isActive || sort?.sortDirection === 'DESC' ? 'ASC' : 'DESC';
    internals.setter((prevState) => ({
      ...prevState,
      sort: { sortField: SORT_FIELD, sortDirection: nextDirection },
    }));
  };

  const icon = !isActive
    ? 'arrow-up-down'
    : sort?.sortDirection === 'ASC'
      ? 'arrow-up'
      : 'arrow-down';

  return (
    <span
      className='cl-sort'
      role='button'
      tabIndex={0}
      onClick={handleSort}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSort();
        }
      }}
    >
      <Trans i18nKey='app.logs.updated_at' />
      <Icon icon={icon} size='xs' />
    </span>
  );
}

export function ChangeLogsTable({
  collection,
  documentId,
  showTitle = true,
  topSlot,
  customerGroupId,
}: {
  collection?: string;
  documentId?: string;
  showTitle?: boolean;
  showHeader?: boolean; // kept for API compat; the count now always sits on the card
  topSlot?: ReactNode;
  customerGroupId?: string; // for distribution policy logs
}) {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  return (
    <Datatable.Container<IChangeLog>
      defaults={{
        limit: 10,
        sort: { sortField: 'loggedAt', sortDirection: 'DESC' },
      }}
      columns={[]}
      fetcher={(query) => {
        const formattedQuery = {
          limit: query.limit,
          page: query.page,
          sortField: query.sort?.sortField ?? 'loggedAt',
          sortDirection: query.sort?.sortDirection ?? 'DESC',
          documentId,
          collection,
          customerGroupId,
        };
        return callCoreApi<IChangeLogList>(
          `/tenants/api/wtb/tenants/${tenantCode}/change-logs?${serialize(
            formattedQuery,
          )}`,
          {},
        );
      }}
      queryKey={ConnectQueryKeys.changeLogs.paginate(collection)}
    >
      {({ data }) => (
        <>
          {showTitle && (
            <div>
              <Title heading='page-title' weight='bold' className='mt-4'>
                <Trans i18nKey='app.logs.history' />
              </Title>
            </div>
          )}
          <Datatable.Background>
            {topSlot}
            <Datatable.Header />
            <div className='cl-list'>
              <div className='cl-head'>
                <span>
                  <Trans i18nKey='app.logs.document_id' />
                </span>
                <span>
                  <Trans i18nKey='app.logs.updated_by' />
                </span>
                <AmendedOnHeader />
              </div>

              {data === undefined && (
                <div className='cl-state'>
                  <Icon icon='spinner' size='sm' />
                </div>
              )}
              {data?.data.length === 0 && (
                <div className='cl-state'>
                  <Text heading='sm' color='secondary-gray'>
                    <Trans i18nKey='app.purchase.search_page.no_results' />
                  </Text>
                </div>
              )}
              {data?.data.map((log) => (
                <ChangeLogEntry
                  collection={collection}
                  log={log}
                  key={log.id}
                />
              ))}
            </div>
            <hr />
            <Datatable.Footer />
          </Datatable.Background>
        </>
      )}
    </Datatable.Container>
  );
}
